// Perfiles (autenticacion.perfiles). Sistemas → Gestión de Autenticación → Perfil.
// CRUD de perfiles con su nivel de acceso. La escritura exige nivel_acceso >= 5
// en el JWT (policy perfiles_write); un usuario de nivel bajo verá error al guardar.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, Modal, Switch, auth, fieldCls } from './shared';

interface Perfil { id: string; nombre: string; descripcion: string | null; nivel_acceso: number | null; activo: boolean; modo_app: boolean }
const vacio = { nombre: '', descripcion: '', nivel_acceso: 1, activo: true, modo_app: false };

// Etiqueta orientativa del nivel (1 = mínimo, 5+ = administrador con permisos de escritura).
const nivelHint = (n: number) => (n >= 5 ? i18n.t('admin:perfiles_nivel_admin') : n >= 3 ? i18n.t('admin:perfiles_nivel_inter') : i18n.t('admin:perfiles_nivel_basico'));

export function Perfiles() {
  const { t } = useTranslation('admin');
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<Perfil[]>({
    queryKey: ['adm-perfiles'],
    queryFn: () => auth.list<Perfil>('perfiles', '?select=id,nombre,descripcion,nivel_acceso,activo,modo_app&order=nivel_acceso.desc,nombre'),
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);
  const [fNombre, setFNombre] = useState('');
  const [fEstado, setFEstado] = useState('todos');
  const filtrados = useMemo(() => rows.filter((p) =>
    (!fNombre.trim() || p.nombre.toLowerCase().includes(fNombre.trim().toLowerCase())) &&
    (fEstado === 'todos' || (fEstado === 'activo' ? p.activo : !p.activo))
  ), [rows, fNombre, fEstado]);

  const abrir = (p?: Perfil) => {
    if (p) { setEditId(p.id); setForm({ nombre: p.nombre, descripcion: p.descripcion ?? '', nivel_acceso: p.nivel_acceso ?? 1, activo: p.activo, modo_app: !!p.modo_app }); }
    else { setEditId(null); setForm(vacio); }
    setAbierto(true);
  };
  const guardar = useMutation({
    mutationFn: () => {
      const body = {
        nombre: form.nombre.trim(),
        descripcion: form.descripcion || null,
        nivel_acceso: Number(form.nivel_acceso) || 1,
        modo_app: !!form.modo_app,
        activo: form.activo,
        updated_at: new Date().toISOString(),
      };
      return editId ? auth.update('perfiles', editId, body) : auth.create('perfiles', body);
    },
    onSuccess: () => { toast.success(t('perfiles_guardado')); setAbierto(false); void qc.invalidateQueries({ queryKey: ['adm-perfiles'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => auth.remove('perfiles', id),
    onSuccess: () => { toast.success(t('perfiles_eliminado')); void qc.invalidateQueries({ queryKey: ['adm-perfiles'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> {t('perfiles_titulo')}</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> {t('perfiles_nuevo')}</Button>
      </div>
      <p className="text-sm text-muted-foreground">
        {t('perfiles_intro_a')}<b>5</b>{t('perfiles_intro_b')}<b>{t('perfiles_intro_c')}</b>.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input className={`${fieldCls} w-64`} placeholder={t('perfiles_filtro_ph')} value={fNombre} onChange={(e) => setFNombre(e.target.value)} />
        <select className={`${fieldCls} w-40`} value={fEstado} onChange={(e) => setFEstado(e.target.value)}>
          <option value="todos">{t('perfiles_todos')}</option><option value="activo">{t('perfiles_activos')}</option><option value="inactivo">{t('perfiles_inactivos')}</option>
        </select>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{t('perfiles_card', { n: filtrados.length })}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">{t('perfiles_c_nombre')}</th><th className="py-2 pr-3">{t('perfiles_c_nivel')}</th><th className="py-2 pr-3">{t('perfiles_c_desc')}</th><th className="py-2 pr-3">{t('comun_estado')}</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {filtrados.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">{t('perfiles_vacio')}</td></tr>}
              {filtrados.map((p) => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-medium">{p.nombre}</td>
                  <td className="py-2 pr-3">
                    <span className="inline-flex items-center gap-1">
                      <span className="font-mono">{p.nivel_acceso ?? 1}</span>
                      <span className="text-xs text-muted-foreground">· {nivelHint(p.nivel_acceso ?? 1)}</span>
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground"><span className="line-clamp-1 max-w-md">{p.descripcion || '—'}</span></td>
                  <td className="py-2 pr-3">{p.activo ? t('comun_activo') : t('comun_inactivo')}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title={t('comun_editar')} onClick={() => abrir(p)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title={t('comun_eliminar')} onClick={async () => { if (await dlg.confirmar(t('perfiles_confirmar_eliminar', { nombre: p.nombre }), { peligro: true })) eliminar.mutate(p.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? t('perfiles_editar') : t('perfiles_nuevo')} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label={t('perfiles_f_nombre')}><input className={fieldCls} maxLength={50} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
            <Campo label={t('perfiles_f_nivel')}>
              <div className="flex items-center gap-3">
                <input type="number" min={1} max={9} className={`${fieldCls} w-24`} value={form.nivel_acceso}
                  onChange={(e) => setForm({ ...form, nivel_acceso: Number(e.target.value) })} />
                <span className="text-xs text-muted-foreground">{t('perfiles_nivel_hint', { hint: nivelHint(Number(form.nivel_acceso) || 1) })}</span>
              </div>
            </Campo>
            <Campo label={t('perfiles_f_desc')}><textarea className={`${fieldCls} h-16`} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></Campo>
            <div className="space-y-2">
              <Switch checked={form.activo} onChange={(v) => setForm({ ...form, activo: v })} label={t('comun_activo')} />
              <Switch checked={form.modo_app} onChange={(v) => setForm({ ...form, modo_app: v })}
                      label={t('perfiles_modo_app')} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>{t('comun_cancelar')}</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim() || Number(form.nivel_acceso) < 1} onClick={() => guardar.mutate()}>{t('comun_guardar')}</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
